import { Injectable, Injector } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ModuleService } from "src/app/services/module.service";

/** One API inside a request group, mirroring the setting-lib catalog shape. */
export interface PageApiEntry {
  apiName: string;
  response: string[];
}

/** One request group configured somewhere on the page. */
export interface PageApiGroup {
  groupName: string;
  apiGroup: PageApiEntry[];
  /** Component the group was configured on — for disambiguating duplicate names. */
  componentName?: string;
}

/** Depth cap: layouts nest deeply and settings blobs can self-reference. */
const MAX_DEPTH = 30;

/** Token name exported by the common setting-lib bundle. */
const CATALOG_SERVICE_EXPORT = "ApiGroupCatalogService";

/**
 * Collects every dynamic-data-provider request group configured anywhere on the
 * current page and publishes it, so that:
 *
 *  - each settings component can receive the page-wide list via `data.pageApiGroups`, and
 *  - the setting-lib's own `ApiGroupCatalogService` (a root-injector singleton
 *    inside the dynamically imported bundle) is fed the same list, which is what
 *    makes the group / API / response-key pickers inside every component offer
 *    groups defined on *other* components.
 *
 * The walk is structural, not schema-bound: any `apiGroupList` array at any depth
 * is picked up, so it keeps working for `getDataFromList` entries, filter rows and
 * whatever nesting gets added later.
 */
@Injectable({ providedIn: "root" })
export class PageApiGroupsService {
  private readonly groupsSubject = new BehaviorSubject<PageApiGroup[]>([]);

  /** Owner token handed to the setting-lib catalog for the page-wide snapshot. */
  private readonly pageOwner = { source: "view-builder-page" };

  constructor(
    private readonly moduleService: ModuleService,
    private readonly injector: Injector,
  ) {}

  readonly groups$: Observable<PageApiGroup[]> = this.groupsSubject.asObservable();

  get groups(): PageApiGroup[] {
    return this.groupsSubject.value;
  }

  /**
   * Re-collects from the page layout and pushes the result to both consumers.
   * Safe to call often — it is a plain in-memory walk.
   */
  refresh(pageLayout: any): PageApiGroup[] {
    const groups = this.collect(pageLayout);
    this.groupsSubject.next(groups);
    this.pushToSettingLib(pageLayout);
    return groups;
  }

  /** Walks the layout and returns every request group found, de-duplicated by name. */
  collect(pageLayout: any): PageApiGroup[] {
    const found: PageApiGroup[] = [];
    this.walk(pageLayout, found, new Set<any>(), 0, undefined);

    const byName = new Map<string, PageApiGroup>();
    found.forEach((group) => {
      if (!group.groupName || byName.has(group.groupName)) return;
      byName.set(group.groupName, group);
    });
    return Array.from(byName.values());
  }

  private walk(
    node: any,
    out: PageApiGroup[],
    seen: Set<any>,
    depth: number,
    componentName: string | undefined,
  ): void {
    if (node == null || typeof node !== "object" || depth > MAX_DEPTH) return;
    if (seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      node.forEach((child) => this.walk(child, out, seen, depth + 1, componentName));
      return;
    }

    // Track which component we're inside so duplicate group names stay traceable.
    const currentComponent = node.componentName ?? componentName;

    Object.keys(node).forEach((key) => {
      const value = node[key];
      if (key === "apiGroupList" && Array.isArray(value)) {
        value.forEach((group: any) => {
          if (!group?.groupName) return;
          out.push({
            groupName: group.groupName,
            componentName: currentComponent,
            apiGroup: (group.apis ?? [])
              .filter((api: any) => api?.apiName)
              .map((api: any) => ({ apiName: api.apiName, response: [] })),
          });
        });
        return; // groups hold apis, not further apiGroupLists
      }
      this.walk(value, out, seen, depth + 1, currentComponent);
    });
  }

  /**
   * Hands the layout to the setting-lib catalog. Resolved lazily and defensively:
   * the bundle is dynamically imported, so on early calls the export simply is not
   * there yet and the pickers fall back to typing a path by hand.
   */
  private pushToSettingLib(pageLayout: any): void {
    const token = this.moduleService.getBundleExport<any>(CATALOG_SERVICE_EXPORT);
    if (!token) return;
    const catalog = this.injector.get(token, null);
    if (!catalog?.syncFromTree) return;
    catalog.syncFromTree(this.pageOwner, pageLayout);
  }
}
